import { Check, ChevronDown, ChevronRight, Files, Plus, Trash2 } from 'lucide-react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-hot-toast';
import { ChronologyDocumentSelectorDialog } from '@/components/knowledge-graph/ChronologyDocumentSelectorDialog';
import { PALETTE } from '@/components/knowledge-graph/chronology-panel/styles';
import { Loading } from '@/components/shared';
import { Button } from '@/components/ui/button';
import { Button as NewButton } from '@/components/ui/NewButton';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import type {
  ChronologyInfo,
  ChronologyTagGroupInfo,
  ChronologyTagInfo,
  TagGroupInput,
  TagInput,
} from '@/hooks/useChronologies';
import { CUSTOM_CHRONOLOGY_DOCUMENT_CAP, SYSTEM_CHRONOLOGY_TYPES, useChronologies } from '@/hooks/useChronologies';
import { useChronologyConfig } from '@/hooks/useChronologyEditing';
import { apiErrorMessage, backendClient } from '@/lib/api-client';
import { useMatterLabels } from '@/lib/useMatterLabels';
import { cn } from '@/lib/utils';

const NAME_MAX = 20;
const DESCRIPTION_MAX = 140;
const TAG_LABEL_MAX = 64;
const TAG_DESCRIPTION_MAX = 160;
const TAG_GROUP_CAP = 10;
const TAG_CAP = 65;
const PROMPT_PLACEHOLDER = `What is this chronology about? Tell Crimson what events to focus on, what to explicitly include, and what to exclude.

For example:
The story of what happened in the real world - contracts signed, payments made, communications between parties.
Include disputed events and any communications about the subject matter.
Exclude procedural litigation events (court filings, hearings, applications) and contract boilerplate.`;
const PALETTE_COLORS = [
  'rose', 'amber', 'slate', 'teal', 'violet', 'emerald', 'orange', 'sky',
  'indigo', 'blue', 'cyan', 'lime', 'fuchsia', 'pink', 'red', 'yellow', 'neutral',
];

interface ChronologyDialogProps {
  matterGuid: string;
  chronology?: ChronologyInfo | null;
  readOnly?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved?: (chronology: ChronologyInfo) => void;
}

interface TagGroupDraft {
  group_key: string;
  label: string;
  color: string | null;
}

interface TagDraft {
  tag_key: string;
  label: string;
  description: string;
  group_key: string;
}

type TagPresetKind = 'factual_background' | 'procedural';

interface TagPresetResponse {
  tag_groups: TagGroupInput[];
  tags: TagInput[];
}

function draftKey(prefix: string): string {
  const suffix = globalThis.crypto?.randomUUID?.().slice(0, 8)
    ?? Math.random().toString(36).slice(2, 10);
  return `${prefix}_${suffix}`;
}

function hydrateGroups(groups: ChronologyTagGroupInfo[] | undefined): TagGroupDraft[] {
  return [...(groups || [])]
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    .map(group => ({
      group_key: group.group_key,
      label: group.label,
      color: group.color,
    }));
}

function hydrateTags(tags: ChronologyTagInfo[] | undefined): TagDraft[] {
  return [...(tags || [])]
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    .map(tag => ({
      tag_key: tag.tag_key,
      label: tag.label,
      description: tag.description ?? '',
      group_key: tag.group_key ?? '',
    }));
}

export function ChronologyDialog({
  matterGuid,
  chronology,
  readOnly = false,
  open,
  onOpenChange,
  onSaved,
}: ChronologyDialogProps) {
  const isEdit = !!chronology;
  const isReadOnly = isEdit && readOnly;
  const chronologyId = chronology?.id;
  const { createChronology, patchChronology } = useChronologies(matterGuid);
  const { config: existingConfig } = useChronologyConfig(matterGuid, isEdit ? chronologyId : undefined);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [prompt, setPrompt] = useState('');
  const [tagGroups, setTagGroups] = useState<TagGroupDraft[]>([]);
  const [tags, setTags] = useState<TagDraft[]>([]);
  const [tagsDirty, setTagsDirty] = useState(false);
  const [collapsedTagGroups, setCollapsedTagGroups] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [presetLoading, setPresetLoading] = useState<TagPresetKind | null>(null);
  const [documentGuids, setDocumentGuids] = useState<string[]>([]);
  const [documentLabels, setDocumentLabels] = useState<Record<string, string>>({});
  const [documentsDirty, setDocumentsDirty] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [scopeLoad, setScopeLoad] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [scopeReload, setScopeReload] = useState(0);
  const needsDocuments = open && (!isEdit || chronology?.document_scope === 'include');
  const tagsOnly = isEdit && SYSTEM_CHRONOLOGY_TYPES.has(chronology?.chronology_type ?? '');
  const documentsScopeReady = !isEdit || scopeLoad === 'ready';

  useEffect(() => {
    if (!open) return;
    if (isEdit && chronology) {
      setName(chronology.name);
      setDescription(chronology.description ?? '');
      setTagGroups(hydrateGroups(chronology.tag_groups));
      setTags(hydrateTags(chronology.tags));
      setCollapsedTagGroups(new Set((chronology.tag_groups || []).map(group => group.group_key)));
      setDocumentGuids([]);
      setDocumentLabels({});
      // Include-mode loading is owned by the fetch effect. Setting it here on
      // chronology-object identity left the section spinning after an SWR
      // refresh that did not restart that fetch.
      if (chronology.document_scope !== 'include') {
        setScopeLoad('ready');
      }
    } else {
      setName('');
      setDescription('');
      setPrompt('');
      setTagGroups([]);
      setTags([]);
      setCollapsedTagGroups(new Set());
      setDocumentGuids([]);
      setDocumentLabels({});
      setScopeLoad('ready');
    }
    setTagsDirty(false);
    setDocumentsDirty(false);
    setPickerOpen(false);
    // Hydrate on open / chronology id, not object identity: an SWR list
    // refresh would otherwise clear in-progress document edits and spin
    // "loading" without restarting the fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, chronologyId, chronology?.document_scope]);

  useEffect(() => {
    if (!open || !isEdit || !chronologyId || !needsDocuments) return;
    let cancelled = false;
    setScopeLoad('loading');
    backendClient.get(
      `/matters/${matterGuid}/chronologies/${chronologyId}/document-scope`,
      { params: { limit: CUSTOM_CHRONOLOGY_DOCUMENT_CAP } },
    ).then((response) => {
      if (cancelled) return;
      const documents = (response.data?.documents || []) as Array<{
        document_guid: string;
        file_name?: string;
      }>;
      const total = Number(response.data?.total ?? documents.length);
      if (total > documents.length) {
        setScopeLoad('error');
        toast.error('Could not load every selected document. Refresh and try again.');
        return;
      }
      const labels: Record<string, string> = {};
      const guids = documents.map((row) => {
        if (row.file_name) labels[row.document_guid] = row.file_name;
        return row.document_guid;
      });
      setDocumentGuids(guids);
      setDocumentLabels(labels);
      setScopeLoad('ready');
    }).catch((err: unknown) => {
      if (cancelled) return;
      setScopeLoad('error');
      toast.error(apiErrorMessage(err) || 'Failed to load chronology documents');
    });
    return () => { cancelled = true; };
  }, [open, isEdit, chronologyId, matterGuid, needsDocuments, scopeReload]);

  useEffect(() => {
    if (!open || !isEdit) return;
    setPrompt((existingConfig.prompt as string | undefined) ?? '');
  }, [open, isEdit, existingConfig]);

  const tagsByGroup = useMemo(() => {
    const map = new Map<string, TagDraft[]>();
    for (const group of tagGroups) map.set(group.group_key, []);
    for (const tag of tags) {
      const bucket = map.get(tag.group_key);
      if (bucket) bucket.push(tag);
    }
    return map;
  }, [tagGroups, tags]);

  const tagCount = tags.length;

  function reset() {
    setName('');
    setDescription('');
    setPrompt('');
    setTagGroups([]);
    setTags([]);
    setTagsDirty(false);
    setCollapsedTagGroups(new Set());
    setPresetLoading(null);
    setDocumentGuids([]);
    setDocumentLabels({});
    setDocumentsDirty(false);
    setPickerOpen(false);
    setScopeLoad('idle');
  }

  function markTagsDirty() {
    if (!isReadOnly) setTagsDirty(true);
  }

  function addGroup() {
    if (isReadOnly) return;
    if (tagGroups.length >= TAG_GROUP_CAP) {
      toast.error(`You can add up to ${TAG_GROUP_CAP} tag groups`);
      return;
    }
    const group_key = draftKey('group');
    setTagGroups(prev => [
      ...prev,
      {
        group_key,
        label: '',
        color: PALETTE_COLORS[prev.length % PALETTE_COLORS.length],
      },
    ]);
    setTagsDirty(true);
    setCollapsedTagGroups(prev => {
      const next = new Set(prev);
      next.delete(group_key);
      return next;
    });
  }

  function updateGroup(groupKey: string, patch: Partial<TagGroupDraft>) {
    if (isReadOnly) return;
    setTagGroups(prev => prev.map(group => group.group_key === groupKey ? { ...group, ...patch } : group));
    markTagsDirty();
  }

  function removeGroup(groupKey: string) {
    if (isReadOnly) return;
    setTagGroups(prev => prev.filter(group => group.group_key !== groupKey));
    setTags(prev => prev.filter(tag => tag.group_key !== groupKey));
    setCollapsedTagGroups(prev => {
      const next = new Set(prev);
      next.delete(groupKey);
      return next;
    });
    setTagsDirty(true);
  }

  function addTag(groupKey: string) {
    if (isReadOnly) return;
    if (tagCount >= TAG_CAP) {
      toast.error(`You can add up to ${TAG_CAP} tags`);
      return;
    }
    setTags(prev => [
      ...prev,
      {
        tag_key: draftKey('tag'),
        label: '',
        description: '',
        group_key: groupKey,
      },
    ]);
    setCollapsedTagGroups(prev => {
      const next = new Set(prev);
      next.delete(groupKey);
      return next;
    });
    setTagsDirty(true);
  }

  function toggleTagGroupCollapsed(groupKey: string) {
    setCollapsedTagGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      return next;
    });
  }

  function updateTag(tagKey: string, patch: Partial<TagDraft>) {
    if (isReadOnly) return;
    setTags(prev => prev.map(tag => tag.tag_key === tagKey ? { ...tag, ...patch } : tag));
    markTagsDirty();
  }

  function removeTag(tagKey: string) {
    if (isReadOnly) return;
    setTags(prev => prev.filter(tag => tag.tag_key !== tagKey));
    setTagsDirty(true);
  }

  async function applyPreset(kind: TagPresetKind) {
    if (isReadOnly || isEdit) return;
    if ((tagGroups.length > 0 || tags.length > 0)
      && !globalThis.confirm('Replace the current tag groups with this preset?')) {
      return;
    }
    setPresetLoading(kind);
    try {
      const response = await backendClient.get<TagPresetResponse>(
        `/matters/${matterGuid}/chronology-tag-presets`,
        { params: { kind } },
      );
      const nextGroups = (response.data.tag_groups || [])
        .slice(0, TAG_GROUP_CAP)
        .map(group => ({
          group_key: group.group_key || draftKey('group'),
          label: group.label,
          color: group.color ?? null,
        }));
      const groupKeys = new Set(nextGroups.map(group => group.group_key));
      const nextTags = (response.data.tags || [])
        .filter(tag => tag.group_key && groupKeys.has(tag.group_key))
        .slice(0, TAG_CAP)
        .map(tag => ({
          tag_key: tag.tag_key || draftKey('tag'),
          label: tag.label,
          description: tag.description ?? '',
          group_key: tag.group_key,
        }));
      setTagGroups(nextGroups);
      setTags(nextTags);
      setCollapsedTagGroups(new Set(nextGroups.map(group => group.group_key)));
      setTagsDirty(true);
    } catch (err: unknown) {
      toast.error(apiErrorMessage(err) || 'Failed to load tag preset');
    } finally {
      setPresetLoading(null);
    }
  }

  function buildTagPayload(): { tag_groups: TagGroupInput[]; tags: TagInput[] } | null {
    if (isEdit && !tagsDirty) return null;
    const groupKeys = new Set(tagGroups.map(group => group.group_key));
    const cleanGroups = tagGroups
      .map(group => ({
        group_key: group.group_key,
        label: group.label.trim(),
        color: group.color,
      }));
    const cleanTags = tags
      .map(tag => ({
        tag_key: tag.tag_key,
        label: tag.label.trim(),
        description: tag.description.trim() || undefined,
        group_key: tag.group_key,
      }))
      .filter(tag => groupKeys.has(tag.group_key));
    return { tag_groups: cleanGroups, tags: cleanTags };
  }

  async function handleSave() {
    if (isReadOnly) return;
    if (!tagsOnly && !name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!tagsOnly && !description.trim()) {
      toast.error('Description is required');
      return;
    }
    if (!tagsOnly && !prompt.trim()) {
      toast.error('Brief is required');
      return;
    }
    const tagPayload = buildTagPayload();
    const shouldValidateTags = tagsDirty || !isEdit;
    if (tagGroups.length > TAG_GROUP_CAP) {
      toast.error(`You can add up to ${TAG_GROUP_CAP} tag groups`);
      return;
    }
    if (tags.length > TAG_CAP) {
      toast.error(`You can add up to ${TAG_CAP} tags`);
      return;
    }
    if (shouldValidateTags && tagGroups.some(group => !group.label.trim())) {
      toast.error('Tag group names cannot be blank');
      return;
    }
    if (shouldValidateTags && tags.some(tag => !tag.label.trim())) {
      toast.error('Tag names cannot be blank');
      return;
    }
    if (shouldValidateTags && tagGroups.some(group => !(tagsByGroup.get(group.group_key) || []).length)) {
      toast.error('Every tag group needs at least one tag');
      return;
    }
    if (tagPayload && tagPayload.tag_groups.length === 0) {
      toast.error('At least one tag group is required');
      return;
    }
    if (tagPayload && tagPayload.tags.length === 0) {
      toast.error('At least one tag is required');
      return;
    }
    const sendingDocuments = needsDocuments && (!isEdit || documentsDirty);
    if (sendingDocuments && documentGuids.length === 0) {
      toast.error('Select the documents that feed this chronology');
      return;
    }
    if (sendingDocuments && documentGuids.length > CUSTOM_CHRONOLOGY_DOCUMENT_CAP) {
      toast.error(`Custom chronologies support at most ${CUSTOM_CHRONOLOGY_DOCUMENT_CAP} documents`);
      return;
    }

    setBusy(true);
    try {
      if (isEdit && !chronologyId) {
        toast.error('Chronology not found');
        return;
      }
      const saved = isEdit && chronologyId
        ? await patchChronology(chronologyId, tagsOnly
          ? { ...(tagPayload || {}) }
          : {
            name: name.trim(),
            description: description.trim(),
            prompt: prompt.trim(),
            ...(tagPayload || {}),
            ...(documentsDirty && needsDocuments ? { document_guids: documentGuids } : {}),
          })
        : await createChronology({
          name: name.trim(),
          description: description.trim(),
          prompt: prompt.trim(),
          ...(tagPayload || {}),
          ...(needsDocuments ? { document_guids: documentGuids } : {}),
        });
      toast.success(isEdit ? `Saved "${saved.name}"` : `Created "${saved.name}"`);
      onSaved?.(saved);
      reset();
      onOpenChange(false);
    } catch (err: unknown) {
      toast.error(apiErrorMessage(err) || `Failed to ${isEdit ? 'save' : 'create'} chronology`);
    } finally {
      setBusy(false);
    }
  }

  const createHasTagConfig = isEdit || (tagGroups.length > 0 && tags.length > 0);
  const documentsBlockingSave = needsDocuments && (
    isEdit ? documentsDirty && documentGuids.length === 0 : documentGuids.length === 0
  );

  return (
    <>
    <Sheet open={open} onOpenChange={(nextOpen: boolean) => { if (!nextOpen) reset(); onOpenChange(nextOpen); }}>
      <SheetContent side="right" className="flex w-full flex-col sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle>{isEdit ? 'Configuration' : 'New chronology'}</SheetTitle>
          {isReadOnly ? (
            <SheetDescription>
              This chronology is system-managed and currently read-only.
            </SheetDescription>
          ) : tagsOnly ? (
            <SheetDescription>
              You can update the tags on this chronology.
            </SheetDescription>
          ) : !isEdit && (
            <SheetDescription>
              Create a focused chronology from a selected set of documents.
            </SheetDescription>
          )}
        </SheetHeader>

        <div className={cn('mt-4 flex-1 space-y-5 overflow-y-auto pr-1', isReadOnly && 'opacity-60')}>
          {!tagsOnly && (
            <div>
              <div className="mb-1 flex items-center justify-between">
                <label className="text-xs font-medium text-gray-500">Name</label>
                <span className="text-[10px] text-gray-400">{name.length}/{NAME_MAX}</span>
              </div>
              <Input
                value={name}
                onChange={(event) => setName(event.target.value.slice(0, NAME_MAX))}
                maxLength={NAME_MAX}
                placeholder="e.g. Money Flow"
                disabled={isReadOnly}
              />
            </div>
          )}

          {!tagsOnly && (
            <div>
              <div className="mb-1 flex items-center justify-between">
                <label className="text-xs font-medium text-gray-500">Description</label>
                <span className="text-[10px] text-gray-400">{description.length}/{DESCRIPTION_MAX}</span>
              </div>
              <Input
                value={description}
                onChange={(event) => setDescription(event.target.value.slice(0, DESCRIPTION_MAX))}
                maxLength={DESCRIPTION_MAX}
                placeholder="Short sentence describing the chronology"
                disabled={isReadOnly}
              />
            </div>
          )}

          {!tagsOnly && (
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-500">Brief</label>
              <textarea
                value={prompt}
                onChange={(event) => setPrompt(event.target.value)}
                rows={8}
                className="flex w-full resize-y rounded-sm border border-input bg-white px-3 py-2 text-sm focus-visible:outline-hidden focus-visible:ring-0"
                placeholder={PROMPT_PLACEHOLDER}
                disabled={isReadOnly}
              />
            </div>
          )}

          {!tagsOnly && needsDocuments && (
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-500">Documents</label>
              <p className="mb-2 text-xs text-gray-500">
                Custom chronologies only use the documents you select, up to {CUSTOM_CHRONOLOGY_DOCUMENT_CAP}.
              </p>
              <div className="flex items-center justify-between gap-3 rounded-sm border border-input bg-white px-3 py-2">
                {isEdit && scopeLoad === 'loading' ? (
                  <Loading size="sm" inline text="Loading selected documents" />
                ) : (
                  <span className="text-sm text-gray-700">
                    {isEdit && scopeLoad === 'error'
                      ? 'Could not load the selected documents'
                      : documentGuids.length === 0
                        ? 'No documents selected'
                        : `${documentGuids.length} document${documentGuids.length === 1 ? '' : 's'} selected`}
                  </span>
                )}
                <div className="flex items-center gap-2">
                  {isEdit && scopeLoad === 'error' && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setScopeReload(count => count + 1)}
                    >
                      Retry
                    </Button>
                  )}
                  <NewButton
                    type="button"
                    variant="outline"
                    size="sm"
                    icon={Files}
                    text="Select documents"
                    onClick={() => setPickerOpen(true)}
                    disabled={isReadOnly || !documentsScopeReady}
                  />
                </div>
              </div>
            </div>
          )}

          <TagConfigEditor
            groups={tagGroups}
            tagsByGroup={tagsByGroup}
            tagCount={tagCount}
            readOnly={isReadOnly}
            onAddGroup={addGroup}
            onUpdateGroup={updateGroup}
            onRemoveGroup={removeGroup}
            onAddTag={addTag}
            onUpdateTag={updateTag}
            onRemoveTag={removeTag}
            collapsedGroups={collapsedTagGroups}
            onToggleGroupCollapsed={toggleTagGroupCollapsed}
            showPresets={!isEdit}
            onApplyPreset={applyPreset}
            presetLoading={presetLoading}
          />
        </div>

        <div className="mt-auto flex justify-end gap-2 pt-6">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={isReadOnly || busy || (tagsOnly ? !tagsDirty : (!name.trim() || !description.trim() || !prompt.trim() || !createHasTagConfig || documentsBlockingSave))}
            title={
              !isEdit && documentsBlockingSave
                ? 'Select the documents that feed this chronology'
                : !isEdit && !createHasTagConfig
                  ? 'Add at least one tag group and tag'
                  : undefined
            }
          >
            {isReadOnly ? 'Read-only' : busy ? 'Saving...' : (isEdit ? 'Save changes' : 'Create')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
    {open && pickerOpen && needsDocuments && documentsScopeReady && (
      <ChronologyDocumentSelectorDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        matterSlug={matterGuid}
        selectedGuids={documentGuids}
        selectedLabels={documentLabels}
        onSave={(next, labels) => {
          setDocumentGuids(next);
          setDocumentLabels(labels);
          setDocumentsDirty(true);
        }}
      />
    )}
    </>
  );
}

function TagConfigEditor({
  groups,
  tagsByGroup,
  tagCount,
  readOnly,
  onAddGroup,
  onUpdateGroup,
  onRemoveGroup,
  onAddTag,
  onUpdateTag,
  onRemoveTag,
  collapsedGroups,
  onToggleGroupCollapsed,
  showPresets,
  onApplyPreset,
  presetLoading,
}: {
  groups: TagGroupDraft[];
  tagsByGroup: Map<string, TagDraft[]>;
  tagCount: number;
  readOnly: boolean;
  onAddGroup: () => void;
  onUpdateGroup: (groupKey: string, patch: Partial<TagGroupDraft>) => void;
  onRemoveGroup: (groupKey: string) => void;
  onAddTag: (groupKey: string) => void;
  onUpdateTag: (tagKey: string, patch: Partial<TagDraft>) => void;
  onRemoveTag: (tagKey: string) => void;
  collapsedGroups: Set<string>;
  onToggleGroupCollapsed: (groupKey: string) => void;
  showPresets: boolean;
  onApplyPreset: (kind: TagPresetKind) => void;
  presetLoading: TagPresetKind | null;
}) {
  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <label className="text-xs font-medium text-gray-500">
          Tags{' '}
          {showPresets && <span className="text-red-500" title="Required">*</span>}
          <span className="text-gray-400">
            {groups.length}/{TAG_GROUP_CAP} groups · {tagCount}/{TAG_CAP} tags
          </span>
        </label>
        <div className="flex items-center gap-1.5">
          {showPresets && (
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-medium text-gray-400">Quick select default tags:</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onApplyPreset('factual_background')}
                disabled={readOnly || presetLoading !== null}
                className="h-7 rounded-sm border-gray-200 bg-white px-2.5 text-xs font-medium text-gray-600 shadow-none hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900"
                title="Seed factual-background tag groups and tags"
                aria-label="Use factual background tag defaults"
              >
                Factual
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onApplyPreset('procedural')}
                disabled={readOnly || presetLoading !== null}
                className="h-7 rounded-sm border-gray-200 bg-white px-2.5 text-xs font-medium text-gray-600 shadow-none hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900"
                title="Seed procedural tag groups and tags"
                aria-label="Use procedural tag defaults"
              >
                Procedural
              </Button>
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={onAddGroup}
            disabled={readOnly || groups.length >= TAG_GROUP_CAP}
            className="h-7 px-2 text-xs"
            title={groups.length >= TAG_GROUP_CAP ? `Maximum ${TAG_GROUP_CAP} groups` : 'Add group'}
          >
            <Plus className="mr-1 h-3 w-3" />
            Group
          </Button>
        </div>
      </div>
      {groups.length > 0 && (
      <div className="divide-y divide-gray-100 rounded-sm border border-gray-200">
        {groups.map(group => {
          const groupTags = tagsByGroup.get(group.group_key) || [];
          const isCollapsed = collapsedGroups.has(group.group_key);
          return (
          <div key={group.group_key}>
            <div className="flex items-center gap-2 bg-gray-50/60 p-2">
              <button
                type="button"
                onClick={() => onToggleGroupCollapsed(group.group_key)}
                className="flex h-6 w-6 items-center justify-center text-gray-400 hover:text-gray-700"
                title={isCollapsed ? 'Expand' : 'Collapse'}
              >
                {isCollapsed
                  ? <ChevronRight className="h-3.5 w-3.5" />
                  : <ChevronDown className="h-3.5 w-3.5" />}
              </button>
              <Input
                value={group.label}
                onChange={(event) => onUpdateGroup(group.group_key, { label: event.target.value.slice(0, TAG_LABEL_MAX) })}
                maxLength={TAG_LABEL_MAX}
                placeholder="Group name"
                disabled={readOnly}
                className="h-8 flex-1 text-sm font-medium"
              />
              <span className="min-w-8 text-right text-[11px] text-gray-400">
                {groupTags.length}
              </span>
              <PalettePicker
                value={group.color ?? null}
                disabled={readOnly}
                onChange={(color) => onUpdateGroup(group.group_key, { color })}
              />
              <button
                type="button"
                onClick={() => onRemoveGroup(group.group_key)}
                disabled={readOnly}
                className="flex h-8 w-8 items-center justify-center rounded-sm text-gray-400 hover:bg-white hover:text-red-500 disabled:cursor-not-allowed disabled:opacity-30"
                aria-label="Remove group"
                title="Remove group"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
            {!isCollapsed && (
            <div className="space-y-1 pb-2 pl-9 pr-2 pt-1">
              {groupTags.map(tag => (
                <div key={tag.tag_key} className="grid grid-cols-[minmax(0,0.9fr)_minmax(0,1.4fr)_2rem] gap-2">
                  <Input
                    value={tag.label}
                    onChange={(event) => onUpdateTag(tag.tag_key, { label: event.target.value.slice(0, TAG_LABEL_MAX) })}
                    maxLength={TAG_LABEL_MAX}
                    placeholder="Tag"
                    disabled={readOnly}
                    className="h-8 text-sm"
                  />
                  <Input
                    value={tag.description}
                    onChange={(event) => onUpdateTag(tag.tag_key, { description: event.target.value.slice(0, TAG_DESCRIPTION_MAX) })}
                    maxLength={TAG_DESCRIPTION_MAX}
                    placeholder="Description"
                    disabled={readOnly}
                    className="h-8 text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => onRemoveTag(tag.tag_key)}
                    disabled={readOnly}
                    className="flex h-8 w-8 items-center justify-center rounded-sm text-gray-400 hover:bg-gray-50 hover:text-red-500 disabled:cursor-not-allowed disabled:opacity-30"
                    aria-label="Remove tag"
                    title="Remove tag"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onAddTag(group.group_key)}
                disabled={readOnly || tagCount >= TAG_CAP}
                className="h-7 px-2 text-xs text-gray-500"
                title={tagCount >= TAG_CAP ? `Maximum ${TAG_CAP} tags` : 'Add tag'}
              >
                <Plus className="mr-1 h-3 w-3" />
                Tag
              </Button>
            </div>
            )}
          </div>
          );
        })}
      </div>
      )}
    </div>
  );
}

function PalettePicker({
  value,
  onChange,
  disabled = false,
}: {
  value: string | null;
  onChange: (color: string | null) => void;
  disabled?: boolean;
}) {
  const label = useMatterLabels();
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const swatchClass = value && PALETTE[value] ? PALETTE[value].dot : PALETTE.neutral.dot;

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const popupWidth = 200;
    const left = Math.max(8, Math.min(window.innerWidth - popupWidth - 8, rect.right - popupWidth));
    setCoords({ top: rect.bottom + 4, left });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        triggerRef.current
        && !triggerRef.current.contains(target)
        && popupRef.current
        && !popupRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    const id = setTimeout(() => document.addEventListener('mousedown', handleMouseDown), 0);
    return () => {
      clearTimeout(id);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(current => !current)}
        disabled={disabled}
        className={cn(
          'h-7 w-7 rounded-md border border-gray-300 transition-shadow hover:shadow-xs disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:shadow-none',
          swatchClass,
        )}
        title={value ?? label('no-colour')}
        aria-label={label('pick-colour')}
      />
      {open && coords && createPortal(
        <div
          ref={popupRef}
          style={{ top: coords.top, left: coords.left, pointerEvents: 'auto' }}
          className="fixed z-[100000] rounded-md border border-gray-200 bg-white p-2 shadow-lg"
        >
          <div className="grid grid-cols-6 gap-1.5">
            {PALETTE_COLORS.map(color => {
              const selected = value === color;
              return (
                <button
                  key={color}
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onChange(color);
                    setOpen(false);
                  }}
                  className={cn(
                    'flex h-6 w-6 items-center justify-center rounded-sm border transition-all',
                    PALETTE[color]?.dot || PALETTE.neutral.dot,
                    selected ? 'border-gray-700 ring-1 ring-gray-700' : 'border-gray-300 hover:border-gray-500',
                  )}
                  title={color}
                  aria-label={`Choose ${color}`}
                >
                  {selected && <Check className="h-3 w-3 text-gray-700" />}
                </button>
              );
            })}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
